// URL-based session resume. When a visitor lands with `?sid=<uuid>` —
// e.g. by clicking their bookmarked personalized pricing link — we
// rehydrate the Zustand store from the server BEFORE React mounts. This
// lets /pricing render with their actual answers/variables/lead-info
// even on a fresh device with empty localStorage.
//
// Called once from main.tsx prior to createRoot().render(). Resolves
// quickly when no sid param is present (no network call). Always
// resolves — never rejects — so a failed lookup doesn't block render.

import { useQuizStore } from '../store/quizStore';
import type { QuizState } from '../store/quizStore';

interface ServerSession {
  client_session_id: string;
  current_question_id: string | null;
  answers: Record<string, unknown>;
  variables: Record<string, string>;
  attribution: Record<string, string>;
  quiz: string | null;
  selected_plan: 'monthly' | 'quarterly' | 'annual' | null;
  started_at: number | null;
  completed_at: number | null;
  lead_id: string | null;
  lead_email: string | null;
  lead_name: string | null;
  lead_phone: string | null;
}

interface ApiResponse {
  session: ServerSession | null;
  error?: string;
}

/**
 * Strip `?sid=...` from the URL without triggering a navigation. Runs
 * after a successful hydration so the URL bar reads cleanly and bookmark
 * shares don't leak the sid into referrer headers further down the funnel.
 */
function stripSidFromUrl(): void {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has('sid')) return;
    u.searchParams.delete('sid');
    const qs = u.search ? u.search : '';
    window.history.replaceState(null, '', u.pathname + qs + u.hash);
  } catch {
    // location is well-formed in any reasonable browser; ignore.
  }
}

/**
 * Reshape the server payload into the Zustand state slice we own. Casts
 * are explicit at this boundary — anything we don't trust gets coerced
 * to a safe default so a malformed row can't crash the store.
 */
function toQuizState(s: ServerSession): Partial<QuizState> {
  return {
    sessionId: s.client_session_id,
    currentQuestionId: s.current_question_id,
    // Answers are stored as { optionIds, freeText?, ts } per question id.
    // We pass through trusting the server-side validate.ts already shaped them.
    answers: (s.answers ?? {}) as QuizState['answers'],
    variables: s.variables ?? {},
    attribution: s.attribution ?? {},
    quiz: s.quiz,
    leadId: s.lead_id,
    leadEmail: s.lead_email,
    leadName: s.lead_name,
    leadPhone: s.lead_phone,
    quizStartedAt: s.started_at,
    quizCompletedAt: s.completed_at,
    selectedPlan: s.selected_plan,
  };
}

/**
 * If the current URL has `?sid=<uuid>`, fetch the server session and
 * overwrite the local store with it. Idempotent — calling more than
 * once is harmless. Returns true if we hydrated, false otherwise.
 */
export async function hydrateFromUrlSid(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  let sid: string;
  try {
    sid = (new URL(window.location.href).searchParams.get('sid') ?? '').trim();
  } catch {
    return false;
  }
  if (!sid || !/^[0-9a-f-]{16,64}$/i.test(sid)) return false;

  // Fast-path: the URL sid matches what's already in the store. No fetch.
  if (useQuizStore.getState().sessionId === sid) {
    stripSidFromUrl();
    return false;
  }

  try {
    const res = await fetch(`/api/session-by-sid?sid=${encodeURIComponent(sid)}`, {
      credentials: 'omit',
    });
    if (!res.ok) return false;
    const json = (await res.json()) as ApiResponse;
    if (!json.session) return false;

    // Overwrite the persisted Zustand state with the server snapshot. We
    // use setState (not actions) so we replace the slice atomically — the
    // store's save-progress subscriber will then no-op since the server
    // state IS what we just wrote.
    useQuizStore.setState(toQuizState(json.session));
    stripSidFromUrl();
    return true;
  } catch {
    return false;
  }
}
